import React from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import { Devices, ArrowBack } from "@mui/icons-material";
import { useMyCredentials } from "../hooks/useMyCredentials";
import { webauthnApi } from "../api/webauthnApi";
import { ORANGE, humanizeError } from "../components/ui";
import LoginHistoryList from "../components/LoginHistoryList";
import { isMultiDevice } from "../deviceSignals";

const MULTI_DEVICE_HINT =
  "This passkey can sync across your devices, so sign-ins may come from more than one physical device.";

const STATUS_COLOR = { active: "success", disabled: "warning", deleted: "default" };

/** Self-service, read-only view of the caller's registered browsers + sign-ins. */
export default function DeviceManagementPage() {
  const nav = useHistory();
  const { list } = useMyCredentials();
  const history = useQuery(["webauthn", "my-login-history"], () => webauthnApi.loginHistory(20));

  const creds = (list.data || []).filter((c) => c.status !== "deleted");

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
        <Tooltip title="Back">
          <IconButton
            onClick={() => nav.goBack()}
            size="small"
            sx={{ border: "1px solid var(--border-1)", color: "var(--text-1)", flexShrink: 0 }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <Devices sx={{ color: ORANGE }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "var(--text-1)" }}>
          Registered browsers
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 14, color: "var(--text-2)", mb: 3 }}>
        These browsers are approved to sign in to your account. Contact an administrator to remove one.
      </Typography>

      {list.isLoading && (
        <Stack alignItems="center" py={4}>
          <CircularProgress sx={{ color: ORANGE }} />
        </Stack>
      )}
      {list.isError && (
        <Alert severity="error" sx={{ borderRadius: "10px" }}>
          {humanizeError(list.error)}
        </Alert>
      )}
      {!list.isLoading && creds.length === 0 && (
        <Typography sx={{ color: "var(--text-2)", fontSize: 14 }}>No browsers registered yet.</Typography>
      )}

      <Stack gap={1.5}>
        {creds.map((c) => (
          <Stack
            key={c.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: "var(--surface-1)",
              border: "1px solid var(--border-1)",
            }}
          >
            <Stack gap={0.25}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>
                  {c.label || "Unnamed browser"}
                </Typography>
                <Chip
                  size="small"
                  label={c.status}
                  color={STATUS_COLOR[c.status] || "default"}
                  sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }}
                />
                {isMultiDevice(c) && (
                  <Tooltip title={MULTI_DEVICE_HINT}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Multi-device"
                      sx={{ height: 20, fontSize: 11, borderColor: ORANGE, color: ORANGE }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography sx={{ fontSize: 12, color: "var(--text-2)" }}>
                Added {fmt(c.created_at)} · Last used {c.last_used_at ? fmt(c.last_used_at) : "never"}
                {c.fingerprint ? ` · #${c.fingerprint}` : ""}
                {c.device_fingerprint ? ` · FP ${String(c.device_fingerprint).slice(0, 8)}` : ""}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>

      {/* Login history */}
      <Divider sx={{ my: 3, borderColor: "var(--border-1)" }} />
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)", mb: 1.5 }}>
        Recent sign-ins
      </Typography>
      {history.isLoading ? (
        <CircularProgress size={20} sx={{ color: ORANGE }} />
      ) : (
        <LoginHistoryList items={history.data} emptyText="No sign-ins recorded yet." />
      )}
    </Box>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (e) {
    return iso;
  }
}
